/**
 * Number and date formats for the Quote-to-Cash dashboard, in the reader's language (en or fr)
 */

const locale = (language: string) =>
  language.startsWith('fr') ? 'fr-CA' : 'en-CA';

/** $125K, $1.24M, US$1.2M (fr: 125 k$, 1,24 M$); CAD unless another currency is given */
export const compactMoney = (
  value: number,
  language: string,
  currency = 'CAD'
) =>
  new Intl.NumberFormat(locale(language), {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(value);

/** $1,234,567, or US$1,234,567 in another currency */
export const fullMoney = (value: number, language: string, currency = 'CAD') =>
  new Intl.NumberFormat(locale(language), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);

/** 14.3% from 0.143 */
export const percent = (value: number, language: string, digits = 1) =>
  new Intl.NumberFormat(locale(language), {
    style: 'percent',
    maximumFractionDigits: digits,
  }).format(value);

/** A sign in front of a positive amount or ratio, as a variance needs */
export const signed = (text: string, value: number) =>
  value > 0 ? `+${text}` : text;

/** A YYYY-MM-DD date as "Oct 14", "Oct 2026" or "October 14, 2026" */
export const formatDate = (
  date: string,
  language: string,
  style: 'day' | 'month' | 'monthYear' | 'long'
) =>
  new Intl.DateTimeFormat(locale(language), {
    timeZone: 'UTC',
    ...{
      day: { month: 'short', day: 'numeric' } as const,
      month: { month: 'short' } as const,
      monthYear: { month: 'short', year: '2-digit' } as const,
      long: { month: 'long', day: 'numeric', year: 'numeric' } as const,
    }[style],
  }).format(new Date(`${date}T00:00:00Z`));
